import { useState } from "react";
import { Sparkles, AlignLeft, FilePlus2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DOC_COLORS, DOC_TYPES } from "@/components/meeting/documents/docTypes";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/lib/i18n";

const TABS = [["summary", Sparkles, "Ringkasan", "Summary"], ["transcript", AlignLeft, "Transkrip", "Transcript"], ["documents", FilePlus2, "Dokumen", "Documents"]];
const SHORT = { official: ["Minit Rasmi", "Official Minutes"], lecture: ["Nota Kuliah", "Lecture Notes"], report: ["Laporan", "Report"], interview: ["Temubual", "Interview"] };

export const NextStepBanner = ({ meeting, canGenerate, onDone }) => {
  const { L } = useApp();
  const [busy, setBusy] = useState("");
  const generate = async (kind) => {
    if (!canGenerate) return onDone(kind);
    setBusy(kind);
    const path = kind === "official" ? `/meetings/${meeting.id}/official` : `/meetings/${meeting.id}/documents/${kind}`;
    try {
      await api.post(path, { output_language: meeting.output_language || "ms" }, { timeout: 180000 });
      toast.success(L(`${SHORT[kind][0]} siap dijana`, `${SHORT[kind][1]} generated`));
      onDone(kind);
    } catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-violet-200 bg-violet-50 p-4 sm:p-5" data-testid="next-step-banner">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-violet-600 text-white"><Sparkles size={20} /></span>
        <div>
          <p className="font-bold text-slate-900">{L("Transkrip siap! Langkah seterusnya:", "Transcript ready! Next step:")}</p>
          <p className="text-sm text-slate-600">{L("Pilih jenis dokumen untuk dijana dengan satu klik.", "Pick a document type to generate in one click.")}</p>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        {DOC_TYPES.map(([k, Icon]) => (
          <button key={k} type="button" disabled={!!busy} onClick={() => generate(k)} data-testid={`banner-generate-${k}`}
            className={`flex items-center gap-2 rounded-xl border px-3 py-3 text-left text-sm font-semibold text-slate-900 transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 ${DOC_COLORS[k][0]} bg-white`}>
            {busy === k ? <Loader2 size={18} className="shrink-0 animate-spin text-violet-600" /> : <Icon size={18} className={`shrink-0 ${DOC_COLORS[k][2]}`} />}
            <span className="min-w-0">{busy === k ? L("Menjana… (~1 minit)", "Generating… (~1 min)") : L(...SHORT[k])}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export const MeetingTabsNav = ({ tab, hasNotes, highlightDocs }) => {
  const { L } = useApp();
  return (
    <TabsList className="flex h-auto w-full justify-start gap-1.5 rounded-2xl bg-slate-100 p-1.5">
      {TABS.map(([k, Icon, ms, en]) => {
        const hot = k === "documents" && highlightDocs && tab !== "documents";
        return (
          <TabsTrigger key={k} value={k} disabled={!hasNotes && k === "summary"} data-testid={`tab-${k}`}
            className={`relative flex-1 gap-1.5 rounded-xl px-2 py-2.5 text-sm font-semibold sm:flex-none sm:gap-2 sm:px-5 sm:text-base ${hot ? "bg-violet-600 text-white shadow-md hover:bg-violet-700" : ""} data-[state=active]:bg-white data-[state=active]:text-violet-700`}>
            <Icon size={18} />{L(ms, en)}
            {hot && <span className="absolute -right-1 -top-1 flex h-3 w-3" data-testid="tab-documents-pulse"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" /><span className="relative inline-flex h-3 w-3 rounded-full bg-amber-500" /></span>}
          </TabsTrigger>
        );
      })}
    </TabsList>
  );
};
